import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import { localStorageGet, LocalStorageKey } from '../../data/client_local_storage.ts';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { alert } from '../../util/confirm.tsx';
import { Button } from './button.tsx';
import {
  getOnboardingContext,
  goToNextOnboardingStep,
  goToOnboardingDestination,
  Message,
  TextInput,
} from './common.tsx';
import { Frame } from './frame.tsx';
import { useCreateHousehold } from './household_create.tsx';
import { nextOnboardingStep } from './onboarding_flow.ts';

export const HouseholdLastName = () => {
  const lastNameInputRef = useRef<HTMLInputElement>(null);
  const [householdEmail] = useLocalStorage(LocalStorageKey.OnboardingHouseholdEmail);
  const navigate = useNavigate();
  const { createHousehold, isCreating } = useCreateHousehold();

  useEffect(() => {
    lastNameInputRef.current?.focus();
  }, [lastNameInputRef]);

  const [lastName, setLastName] = useLocalStorage(LocalStorageKey.OnboardingLastName);
  const onLastNameChange = useCallback(() => {
    setLastName(lastNameInputRef.current?.value);
  }, [setLastName]);

  const onContinue = useCallback(async () => {
    if (!lastNameInputRef.current?.value) {
      await alert({ confirmation: <span>Please enter your last name</span> });
      return;
    }
    const next = nextOnboardingStep(
      { from: OnboardingPages.HouseholdLastName, action: 'continue', householdEmailKnown: Boolean(householdEmail) },
      getOnboardingContext(),
    );
    if (next.kind !== 'createHousehold') {
      await goToOnboardingDestination(navigate, next);
      return;
    }
    // A promotional campaign's visitor confirmed the email already (GitHub issue #3551). Coming back
    // here after creating reuses that household rather than creating it twice.
    const household = localStorageGet(LocalStorageKey.Household);
    if (household?.householdEmail?.toLowerCase() === householdEmail?.toLowerCase()) {
      await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdCreate, action: 'created' });
      return;
    }
    await createHousehold();
  }, [createHousehold, householdEmail, navigate]);

  return (
    <Frame
      onboardingPage={OnboardingPages.HouseholdLastName}
      mainContent={
        <>
          <Message $widthInChars={18}>What is your last name?</Message>
          <TextInput
            type="text"
            enterKeyHint="next"
            defaultValue={lastName}
            onChange={onLastNameChange}
            ref={lastNameInputRef}
            autoCapitalize="words"
          />
        </>
      }
      footerContent={
        <Button type="submit" onClick={onContinue} disabled={!lastName} busy={isCreating}>
          CONTINUE
        </Button>
      }
      onEnterKeypress={onContinue}
      containsInputElement={true}
    />
  );
};
